import { useEffect, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  CircularProgress,
  useTheme,
  Button,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ReportIcon from "@mui/icons-material/Report";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  fetchAwaitingApprovalComments,
  approveComment,
  spamComment,
  deleteComment,
  AwaitingApprovalComment,
} from "../../stream-management/components/memorial-page/comments-compiler/service/memoryBook.api";
import { tokens } from "../../../theme";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../components/common/ShowToast";

const CLAMP_LINES = 3;

function CommentContent({
  content,
  colors,
}: {
  content: string;
  colors: ReturnType<typeof tokens>;
}) {
  const [expanded, setExpanded] = useState(false);
  const isLong = content.length > 180;

  return (
    <Box>
      <Typography
        variant="body2"
        color={colors.primary[700]}
        sx={
          !expanded && isLong
            ? {
                mt: 0.5,
                display: "-webkit-box",
                WebkitLineClamp: CLAMP_LINES,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }
            : { mt: 0.5 }
        }
      >
        {content}
      </Typography>
      {isLong && (
        <Button
          size="small"
          variant="text"
          onClick={() => setExpanded((v) => !v)}
          sx={{
            p: 0,
            minWidth: 0,
            fontSize: "0.7rem",
            fontWeight: 700,
            color: colors.primary[400],
            mt: 0.25,
          }}
        >
          {expanded ? "Read less" : "Read more"}
        </Button>
      )}
    </Box>
  );
}

export default function CommentsAwaitingApproval() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [comments, setComments] = useState<AwaitingApprovalComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);

  const [busyId, setBusyId] = useState<number | null>(null);

  const loadComments = useCallback(async (targetPage: number) => {
    try {
      setCommentsLoading(true);
      const res = await fetchAwaitingApprovalComments(targetPage);
      const data = res.data;
      setComments(data?.results ?? []);
      setTotalCount(data?.count ?? 0);
      setHasNext(!!data?.next);
      setHasPrev(!!data?.previous);
    } catch {
      showErrorToast("Failed to load pending comments");
    } finally {
      setCommentsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadComments(page);
  }, [page, loadComments]);

  const removeFromList = (commentId: number) => {
    setComments((prev) => {
      const updated = prev.filter((c) => c.id !== commentId);
      setTotalCount((t) => t - 1);

      if (updated.length === 0 && page > 1) {
        setPage((p) => p - 1);
      } else if (updated.length === 0) {
        // stay on page 1, list is just empty now
      }
      return updated;
    });
  };

  const handleApprove = async (commentId: number) => {
    setBusyId(commentId);
    try {
      await approveComment(commentId);
      removeFromList(commentId);
      showSuccessToast("Comment approved");
    } catch {
      showErrorToast("Failed to approve comment");
    } finally {
      setBusyId(null);
    }
  };

  const handleSpam = async (commentId: number) => {
    setBusyId(commentId);
    try {
      await spamComment(commentId);
      removeFromList(commentId);
      showSuccessToast("Comment marked as spam");
    } catch {
      showErrorToast("Failed to mark comment as spam");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (commentId: number) => {
    setBusyId(commentId);
    try {
      await deleteComment(commentId);
      removeFromList(commentId);
      showSuccessToast("Comment deleted");
    } catch {
      showErrorToast("Failed to delete comment");
    } finally {
      setBusyId(null);
    }
  };

  const pageSize = 10;
  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "24px",
        p: 3,
        height: "100%",
        backgroundColor: colors.primary[900],
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6" fontWeight={700} color={colors.primary[700]}>
          Pending Comments
        </Typography>

        {totalCount > 0 && (
          <Typography
            variant="caption"
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: "1000px",
              backgroundColor: "#FF0000",
              color: colors.primary[900],
              fontWeight: 700,
            }}
          >
            {totalCount}
          </Typography>
        )}
      </Box>

      {/* Body */}
      {commentsLoading ? (
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress size={28} sx={{ color: colors.primary[400] }} />
        </Box>
      ) : comments.length === 0 ? (
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" color={colors.primary[300]}>
            No comments awaiting approval.
          </Typography>
        </Box>
      ) : (
        <Stack
          spacing={0}
          divider={<Divider sx={{ borderColor: `${colors.primary[800]}80` }} />}
          sx={{
            flexGrow: 1,
            minHeight: 0,
            overflowY: "auto",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {comments.map((comment) => {
            const isBusy = busyId === comment.id;

            return (
              <Box key={comment.id} sx={{ py: 1.5 }}>
                {/* Subject title */}
                {comment.subject_title && (
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{
                      color: colors.primary[500],
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}
                  >
                    {comment.subject_title}
                  </Typography>
                )}

                {/* Comment content with read more */}
                <CommentContent content={comment.content} colors={colors} />

                {/* Footer: sender + actions */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mt: 1,
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    color={colors.primary[400]}
                    noWrap
                    sx={{ flexShrink: 1, minWidth: 0 }}
                  >
                    {comment.sender_full_name}
                  </Typography>

                  {isBusy ? (
                    <CircularProgress size={16} sx={{ flexShrink: 0 }} />
                  ) : (
                    <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                      <Tooltip title="Approve">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleApprove(comment.id)}
                          sx={{ p: 0.5 }}
                        >
                          <CheckCircleOutlineIcon sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Mark as spam">
                        <IconButton
                          size="small"
                          onClick={() => handleSpam(comment.id)}
                          sx={{ p: 0.5, color: "#F57C00" }}
                        >
                          <ReportIcon sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(comment.id)}
                          sx={{ p: 0.5 }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Stack>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: 2,
            pt: 2,
            borderTop: `1px solid ${colors.primary[800]}80`,
          }}
        >
          <IconButton
            size="small"
            disabled={!hasPrev || commentsLoading}
            onClick={() => setPage((p) => p - 1)}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>

          <Typography variant="caption" color={colors.primary[400]}>
            {page} / {totalPages}
          </Typography>

          <IconButton
            size="small"
            disabled={!hasNext || commentsLoading}
            onClick={() => setPage((p) => p + 1)}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Paper>
  );
}
